/**
 * Componente: Personal (usuarios del sistema — staff municipal)
 * Reemplaza el alta a mano en la base: admin-only, alta con activación por
 * mail (el operador elige su propia contraseña, nadie más la conoce).
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import PersonalFormModal from './PersonalFormModal';
import ConfirmarAccionPersonalModal from './ConfirmarAccionPersonalModal';
import { formatearFecha } from '../utils/fecha';
import {
  STROKE_DEGRADE,
  IconAgregar,
  IconExito,
  IconRechazar,
  IconAdvertencia,
  IconEditar,
  IconEmail,
  IconEliminar,
} from './icons';
import '../styles/padron.css';

const API_URL = process.env.REACT_APP_API_URL;

// Mismo criterio que Padron.jsx: íconos de botón de fila chicos, trazo en
// degradé de marca, sin insignia circular.
const ICONO_BOTON = { size: 14, stroke: STROKE_DEGRADE };

const ESTADOS_PERSONAL = {
  activo: { label: 'Activo', clase: 'activo' },
  pendiente: { label: 'Pendiente de activación', clase: 'pendiente' },
  desactivado: { label: 'Desactivado', clase: 'desactivado' },
  eliminado: { label: 'Eliminado', clase: 'eliminado' },
};

const ROLES_LABEL = { admin: 'Administrador', operador: 'Operador' };

// El Admin Principal está por encima de cualquier otro admin: nadie desde Personal puede editarlo, desactivarlo,
// darlo de baja ni reenviarle la invitación (lo frena el backend con un 403; acá solo se ocultan los botones). El
// texto lo manda el backend en GET /api/personal; este es el respaldo si no llegara.
// En Personal nadie modifica su propia cuenta (ni editarla, ni desactivarse, ni eliminarse): lo hace el Admin Principal. El
// servidor lo rechaza igual con un 403 (PUT) o un 400 (desactivar / eliminar).
export const MENSAJE_CUENTA_PROPIA = 'Tu propia cuenta no se modifica desde acá. Pedile al Admin Principal que lo haga.';

// Id de quien tiene la sesión abierta (lo guarda el login en sessionStorage). null si no se puede leer.
const idDeLaSesion = () => {
  try {
    const id = JSON.parse(sessionStorage.getItem('usuario'))?.id;
    return id === undefined || id === null ? null : Number(id);
  } catch (e) {
    return null;
  }
};

export const MENSAJE_ADMIN_PRINCIPAL =
  'Solo Betech puede modificar al Admin Principal. Para cambiarlo, el municipio debe presentar una nota firmada por su responsable.';

const Personal = ({ onVolver }) => {
  const [personal, setPersonal] = useState([]);
  // Límites efectivos (ya resueltos: override del municipio si existe, si no
  // el default general) que devuelve GET /api/personal — null hasta que
  // carga la primera vez. Ver limite_admins/limite_operadores en server.js.
  const [limites, setLimites] = useState({ admins: null, operadores: null });
  const [mensajePrincipal, setMensajePrincipal] = useState(MENSAJE_ADMIN_PRINCIPAL);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editandoPersona, setEditandoPersona] = useState(null);
  const [procesandoId, setProcesandoId] = useState(null);
  // { tipo: 'desactivar'|'reactivar'|'eliminar', persona } — reemplaza el
  // confirm() nativo (mismo bug ya visto antes con "Eliminar diseño").
  const [confirmacion, setConfirmacion] = useState(null);

  // En la fila propia no se ofrece ningún botón.
  const miId = idDeLaSesion();

  const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('token')}` });

  const cargarPersonal = useCallback(async () => {
    try {
      setCargando(true);
      setError('');
      const { data } = await axios.get(`${API_URL}/personal`, { headers: authHeaders() });
      setPersonal(data.personal || []);
      setLimites({ admins: data.limite_admins ?? null, operadores: data.limite_operadores ?? null });
      setMensajePrincipal(data.mensaje_admin_principal || MENSAJE_ADMIN_PRINCIPAL);
    } catch (err) {
      console.error('Error al cargar el personal:', err);
      setError(err.response?.data?.mensaje || 'Error al cargar el personal');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarPersonal();
  }, [cargarPersonal]);

  const abrirNuevo = () => {
    setEditandoPersona(null);
    setModalAbierto(true);
  };

  const abrirEditar = (persona) => {
    setEditandoPersona(persona);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setEditandoPersona(null);
  };

  const alGuardar = (mensajeConfirmacion) => {
    cerrarModal();
    setMensaje(mensajeConfirmacion || '');
    cargarPersonal();
    if (mensajeConfirmacion) {
      setTimeout(() => setMensaje(''), 6000);
    }
  };

  const pedirCambioEstado = (persona) => {
    setConfirmacion({ tipo: persona.estado === 'desactivado' ? 'reactivar' : 'desactivar', persona });
  };

  const pedirEliminar = (persona) => {
    setConfirmacion({ tipo: 'eliminar', persona });
  };

  const cancelarConfirmacion = () => setConfirmacion(null);

  const confirmarAccion = async () => {
    if (!confirmacion) return;
    const { tipo, persona } = confirmacion;

    try {
      setProcesandoId(persona.id);
      setError('');
      if (tipo === 'eliminar') {
        await axios.delete(`${API_URL}/personal/${persona.id}`, { headers: authHeaders() });
      } else {
        await axios.put(
          `${API_URL}/personal/${persona.id}/estado`,
          { activo: tipo === 'reactivar' },
          { headers: authHeaders() }
        );
      }
      setConfirmacion(null);
      await cargarPersonal();
    } catch (err) {
      console.error('Error al procesar la acción:', err);
      setError(err.response?.data?.mensaje || 'Error al procesar la acción');
      setConfirmacion(null);
    } finally {
      setProcesandoId(null);
    }
  };

  const reenviarInvitacion = async (persona) => {
    try {
      setProcesandoId(persona.id);
      setError('');
      await axios.post(`${API_URL}/personal/${persona.id}/reenviar-invitacion`, {}, { headers: authHeaders() });
      setMensaje(`Se reenvió la invitación a ${persona.email}`);
      setTimeout(() => setMensaje(''), 6000);
    } catch (err) {
      console.error('Error al reenviar la invitación:', err);
      setError(err.response?.data?.mensaje || 'Error al reenviar la invitación');
    } finally {
      setProcesandoId(null);
    }
  };

  return (
    <section className="padron-section">
      {onVolver && (
        <button type="button" className="btn-volver padron-btn-volver" onClick={onVolver}>
          <ArrowLeft size={16} /> Volver
        </button>
      )}

      <div className="padron-toolbar">
        <div>
          <h2 style={{ margin: 0 }}>Personal</h2>
          <p className="form-hint" style={{ marginTop: 4 }}>
            {limites.admins != null && limites.operadores != null ? (
              <>
                Hasta {limites.admins} administrador{limites.admins === 1 ? '' : 'es'} y {limites.operadores}{' '}
                operador{limites.operadores === 1 ? '' : 'es'} por municipio.
              </>
            ) : (
              'Límites de personal por municipio.'
            )}
          </p>
        </div>
        <button className="btn-nuevo-contribuyente" onClick={abrirNuevo}>
          <IconAgregar size={16} /> Nuevo
        </button>
      </div>

      {mensaje && (
        <div className="alert alert-success">
          <IconExito size={18} />
          <p>{mensaje}</p>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <IconAdvertencia size={18} />
          <p>{error}</p>
        </div>
      )}

      {cargando ? (
        <div className="padron-loading">
          <div className="loading-spinner"></div>
          <p>Cargando personal...</p>
        </div>
      ) : personal.length === 0 ? (
        <div className="padron-empty">
          <p>Todavía no hay nadie cargado.</p>
        </div>
      ) : (
        <div className="padron-table-wrapper">
          <table className="padron-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Fecha de alta</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {personal.map((p) => (
                <tr key={p.id}>
                  <td data-label="Nombre">
                    {p.nombre_completo}
                    {p.es_principal && <span className="badge-personal badge-personal-principal">Admin Principal</span>}
                  </td>
                  <td data-label="Email">{p.email || '—'}</td>
                  <td data-label="Rol">{ROLES_LABEL[p.rol] || p.rol}</td>
                  <td data-label="Estado">
                    <span className={`badge-personal badge-personal-${ESTADOS_PERSONAL[p.estado].clase}`}>
                      {ESTADOS_PERSONAL[p.estado].label}
                    </span>
                  </td>
                  <td data-label="Fecha de alta">{formatearFecha(p.fecha_alta?.slice(0, 10))}</td>
                  <td data-label="Acciones">
                    {p.estado === 'eliminado' ? (
                      <span className="form-hint">Sin acciones disponibles</span>
                    ) : p.es_principal ? (
                      <span className="form-hint personal-principal-aviso">{mensajePrincipal}</span>
                    ) : p.id === miId ? (
                      <span className="form-hint personal-principal-aviso">{MENSAJE_CUENTA_PROPIA}</span>
                    ) : (
                      <div className="padron-acciones">
                        <button className="btn-accion btn-editar" onClick={() => abrirEditar(p)}>
                          <IconEditar {...ICONO_BOTON} /> Editar
                        </button>
                        {p.estado === 'pendiente' && (
                          <button
                            className="btn-accion btn-editar"
                            onClick={() => reenviarInvitacion(p)}
                            disabled={procesandoId === p.id}
                          >
                            <IconEmail {...ICONO_BOTON} /> {procesandoId === p.id ? 'Enviando...' : 'Reenviar invitación'}
                          </button>
                        )}
                        <button
                          className={`btn-accion ${p.estado === 'desactivado' ? 'btn-editar' : 'btn-imprimir'}`}
                          onClick={() => pedirCambioEstado(p)}
                          disabled={procesandoId === p.id}
                        >
                          {p.estado === 'desactivado' ? (
                            <><IconExito {...ICONO_BOTON} /> Reactivar</>
                          ) : (
                            <><IconRechazar {...ICONO_BOTON} /> Desactivar</>
                          )}
                        </button>
                        <button
                          className="btn-accion btn-imprimir"
                          onClick={() => pedirEliminar(p)}
                          disabled={procesandoId === p.id}
                        >
                          <IconEliminar {...ICONO_BOTON} /> Eliminar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalAbierto && (
        <PersonalFormModal
          personaInicial={editandoPersona}
          onClose={cerrarModal}
          onSaved={alGuardar}
        />
      )}

      {confirmacion && (
        <ConfirmarAccionPersonalModal
          tipo={confirmacion.tipo}
          nombre={confirmacion.persona.nombre_completo}
          procesando={procesandoId === confirmacion.persona.id}
          onConfirmar={confirmarAccion}
          onCancelar={cancelarConfirmacion}
        />
      )}
    </section>
  );
};

export default Personal;
